import { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { useParams } from 'react-router-dom';

import type { MetagameTokenPackageBundle } from '../../../dto/metagames.types';
import { useFlag } from '../../../services/launchDarkly';
import { platformContextService } from '../../../services/platformContext/PlatformContextService';
import { isBingoCardsPackageBundles } from '../../../stores/bingo/helpers';
import { isCoinBackPackageBundles } from '../../../stores/coin-back/helpers';
import { isMetagameTokenPackageBundles } from '../../../stores/metagames/helpers';
import { isChallengeBundleItem, isFreeSpinsBundlePayload, isMetaGameBundleItem } from '../../../stores/rewards/helpers';
import type { BundlePayload } from '../../../stores/rewards/types';
import { SubscriptionUtils } from '../../../stores/subscription/utils';
import { useStores } from '../../../stores/useStores';
import { PaymentRouterProviderEnum } from '../../../types';
import { getIframeMaxHeight } from '../shared/helpers';

import type { PaymentServicePurchasePackage, PaymentsProps } from './types';

export function useIFrameParams(
  props: PaymentsProps,
  payComSessionData?: { sessionId: string; clientSessionSecretKey: string } | null,
) {
  const { loyaltyProgramStore, appStore, userStore } = useStores();
  const { gameId } = useParams();
  const { currentOrderPackage, selectedPackage } = appStore;

  const paymentRouter = useFlag<PaymentRouterProviderEnum>('payment_router', PaymentRouterProviderEnum.SUNFLOWER);

  return useMemo(() => {
    if (isEmpty(selectedPackage)) {
      return undefined;
    }
    const { packageId, price, gcAmount, scAmount, packageBundles } = selectedPackage;

    const purchaseSource = sessionStorage.getItem('purchaseSource');
    const params = new URLSearchParams();
    const activeEventAllowedKeys = ['userEventTriggerId'] as const;
    const activeEventBenefitAllowedKeys = ['benefitType', 'key', 'offerId', 'offerType'] as const;
    const activeEventEventTriggerAllowedKeys = ['triggerKey', 'conditionsKey', 'eventTriggerId', 'department'] as const;

    const { platform, paymentEmitterPlatform } = platformContextService.getConfig();

    params.set('platform', platform);

    if (paymentEmitterPlatform) {
      params.set('paymentEmitterPlatform', paymentEmitterPlatform);
    }

    params.set('storeType', props.storeType);
    params.set('storeSegment', loyaltyProgramStore.currentTierKey);

    params.set('packageId', `${packageId}`);
    params.set('amount', String(price));
    params.set('language', userStore.profile?.language || '');
    params.set('country', userStore.profile?.territory?.countryCode || '');
    params.set('currency', userStore.profile?.currency.code || '');
    params.set('paymentRouter', paymentRouter);

    if (paymentRouter === PaymentRouterProviderEnum.PAY_COM && payComSessionData) {
      params.set('payComSessionId', payComSessionData.sessionId);
      params.set('payComClientSecret', payComSessionData.clientSessionSecretKey);
    }

    if (selectedPackage.subscriptionData) {
      const subscription = selectedPackage.subscriptionData;
      const quantity = subscription.quantity || 1;

      const packageInfo = {
        packageId: subscription.packageId,
        price: subscription.price * quantity,
        ccCards: SubscriptionUtils.getCardsCounts(subscription.benefitPackage.packageBundles, 'GC') * quantity,
        sweepsCards: SubscriptionUtils.getCardsCounts(subscription.benefitPackage.packageBundles, 'CASH') * quantity,
        ccCurrencySymbol: SubscriptionUtils.getCurrencySymbol(subscription.benefitPackage.packageBundles, 'GC'),
        sweepsCurrencySymbol: SubscriptionUtils.getCurrencySymbol(subscription.benefitPackage.packageBundles, 'CASH'),
        subscriptionType: subscription.type || 'subscription',
        billingCycle: subscription.cycleType,
        subscriptionName: subscription.name,
        quantity,
      };
      params.set('package', JSON.stringify(packageInfo));
      params.set('planId', subscription.id);
      params.set('packageType', 'subscription');
    } else {
      const isBingoCards =
        packageBundles && isMetagameTokenPackageBundles(packageBundles) && isBingoCardsPackageBundles(packageBundles);
      const metagamesTokenBundles = (packageBundles?.filter((bundle) => isMetaGameBundleItem(bundle as any)) ??
        []) as MetagameTokenPackageBundle[];
      const freeSpinsBundles = (packageBundles?.filter((bundle) =>
        isFreeSpinsBundlePayload(bundle.bundlePayload as BundlePayload),
      ) ?? []) as any[];
      const challengesBundles = (packageBundles?.filter((bundle) => isChallengeBundleItem(bundle as any)) ??
        []) as any[];

      const purchasePackage: PaymentServicePurchasePackage = {
        packageId: Number(packageId),
        price: Number(price),
        gcCoin: Number(gcAmount),
        scCoin: Number(scAmount),
        ...(isBingoCards && {
          ccCards: SubscriptionUtils.getCardsCounts(packageBundles, 'GC'),
          sweepsCards: SubscriptionUtils.getCardsCounts(packageBundles, 'CASH'),
          ccCurrencySymbol: SubscriptionUtils.getCurrencySymbol(packageBundles, 'GC'),
          sweepsCurrencySymbol: SubscriptionUtils.getCurrencySymbol(packageBundles, 'CASH'),
        }),
        ...(packageBundles && isCoinBackPackageBundles(packageBundles)
          ? {
              coinBack: packageBundles.find((bundle) => bundle.bundleType === 'coinBack')?.bundlePayload,
            }
          : {}),
        ...(metagamesTokenBundles.length > 0
          ? {
              metagamesTokensBundles: metagamesTokenBundles.map((bundle) => ({
                id: bundle.id,
                amount: Number(bundle.bundlePayload.amount),
                currencySymbol: bundle.bundlePayload.currencySymbol,
                coinType: bundle.bundlePayload.coinType,
              })),
            }
          : {}),
        ...(freeSpinsBundles.length > 0
          ? {
              freeSpinsBundles: freeSpinsBundles.map((bundle) => ({
                id: bundle.id,
                gameImageUrl: bundle.bundlePayload.gameImageUrl,
                rounds: bundle.bundlePayload.rounds,
                coinType: bundle.bundlePayload.coinType,
              })),
            }
          : {}),
        ...(challengesBundles.length > 0
          ? {
              challengesBundles: challengesBundles.map((bundle) => ({
                symbolUrl: bundle.bundlePayload.symbolUrl,
              })),
            }
          : {}),
      };

      params.set('package', JSON.stringify(purchasePackage));
    }
    params.set('maxHeight', `${getIframeMaxHeight()}`);

    if (currentOrderPackage?.eventTriggerData && currentOrderPackage.eventTriggerData.activeEvent) {
      params.set(
        'eventTriggerData',
        JSON.stringify({
          activeEvent: {
            ...pick(currentOrderPackage.eventTriggerData.activeEvent, activeEventAllowedKeys),
            benefit: {
              ...pick(currentOrderPackage.eventTriggerData.activeEvent.benefit, activeEventBenefitAllowedKeys),
            },
            eventTrigger: {
              ...pick(
                currentOrderPackage.eventTriggerData.activeEvent.eventTrigger,
                activeEventEventTriggerAllowedKeys,
              ),
            },
          },
        }),
      );

      params.set('packageType', 'offer');
    }

    if (currentOrderPackage?.eventTriggerData?.activeEvent?.benefit?.benefitType === 'road') {
      params.set('packageType', 'road');
    }

    if (gameId) {
      params.set('gameId', gameId);
    }

    if (purchaseSource) {
      params.set('purchaseSource', purchaseSource);
    }

    return params.toString();
  }, [
    currentOrderPackage,
    gameId,
    loyaltyProgramStore.currentTierKey,
    props.storeType,
    selectedPackage,
    userStore.profile,
    paymentRouter,
    payComSessionData,
  ]);
}
